import type { FormInstance } from 'antd'
import { flatten, pick } from 'lodash'
import { createSlice } from '@reduxjs/toolkit'
import { fetchAreaList, getAddressListChildren, getProvinceList } from '@/services/apis'
import type { AppDispatch, AppGetState } from '@/store'
import { updateAddressVO } from '@/store/newServiceProviderSlice'
import { decodeLanguageAndProvince } from '@/utils/address'
import type * as Types from './types'

export const initialState = {
  provinces: [],
  cities: [],
  districts: [],
  towns: [],
  loading: false,
  language: '',
}

export const senderAddressSlice = createSlice({
  name: 'senderAddressForm',
  initialState,
  reducers: {
    update(state, action) {
      const { payload } = action
      return { ...state, ...payload }
    },
    clearSenderAddress(state) {
      return { ...state, ...pick(initialState, ['provinces', 'cities', 'districts', 'towns']) }
    },
  },
})

export const { actions, reducer } = senderAddressSlice
export default reducer
export const { clearSenderAddress } = actions
/**
 * 初始化发货国家
 * 只会设置name为 departure_ 开头的表单值
 * @param params
 * @returns
 */
export const initSenderAddressForm =
  (params?: { formValues?: Types.ISenderAddressFormValues }) => async (dispatch: AppDispatch, _getState: AppGetState, form: FormInstance<Types.ISenderAddressFormValues>) => {
    const { formValues = {} } = params || {}
    const { departure_province, departure_city, departure_district } = formValues

    const { departure_countryCode } = form.getFieldsValue()
    departure_countryCode &&
      (await dispatch(
        fetchSenderAddressProvinceListNew({
          countryCode: departure_countryCode,
        })
      ))
    departure_countryCode && departure_province && (await dispatch(selectSenderAddressProvinceNew(departure_province)))
    departure_countryCode && departure_province && departure_city && (await dispatch(selectSenderAddressCityNew(departure_city)))
    departure_countryCode && departure_province && departure_city && departure_district && (await dispatch(selectSenderAddressDistrictNew(departure_district)))

    // 最后再执行一次，比如 city 会清空 district
    dispatch(
      updateAddressVO({
        departure_city: formValues?.departure_city,
        departure_district: formValues?.departure_district,
        departure_town: formValues?.departure_town,
        departure_province: formValues?.departure_province,
        departure_zipCode: formValues?.departure_zipCode,
        departure_address: formValues?.departure_address,
        departure_addressTwo: formValues?.departure_addressTwo,
        departure_fullName: formValues.departure_fullName,
        departure_mobile: formValues.departure_mobile,
      })
    )
    const updateFormValues = pick(
      formValues,
      Object.keys(formValues).filter((key) => key.startsWith('departure_'))
    )

    form.setFieldsValue(updateFormValues)
  }

/** 获取省列表 */
export const fetchSenderAddressProvinceList = (params?: Types.FetchDepartureAreaListParams) => async (dispatch: AppDispatch) => {
  const provinces = await fetchAreaList(params)
  dispatch(actions.update({ provinces }))
}

/** 获取省列表 */
export const fetchSenderAddressProvinceListNew = (params?: Parameters<typeof getProvinceList>[0]) => async (dispatch: AppDispatch) => {
  const data = await getProvinceList(params)
  const provinces = data?.data?.results || []
  dispatch(actions.update({ provinces }))

  return provinces
}

/** 获取发货可视配置 */
export const getSenderAddressVisibility = () => (_dispatch: AppDispatch, getState: AppGetState, form: FormInstance) => {
  const { address } = getState()
  const countryCode = form.getFieldValue('departure_countryCode')
  const addressConfig = address?.addressConfig?.[countryCode]

  /* 只有省份隐藏 */
  const isProvinceHidden = !addressConfig.provinceVisibility && addressConfig.cityVisibility && addressConfig.districtVisibility

  /* 只有城市隐藏 */
  const isCityHidden = !addressConfig.cityVisibility && addressConfig.provinceVisibility && addressConfig.districtVisibility

  /* 只有区显示 */
  const isOnlyDistrictShow = addressConfig.districtVisibility && !addressConfig.provinceVisibility && !addressConfig.cityVisibility

  return { isProvinceHidden, isCityHidden, isOnlyDistrictShow }
}

/** 选择发货省 */
export const selectSenderAddressProvinceNew = (value: string) => async (dispatch: AppDispatch, getState: AppGetState, form: FormInstance) => {
  const { isCityHidden } = dispatch(getSenderAddressVisibility())

  const { departure_countryCode } = form.getFieldsValue()

  const { province, language } = decodeLanguageAndProvince(value)

  const cities = await (async () => {
    // 没有语言和国家，请求也会报错
    if (!language || !departure_countryCode) return []

    const citiesResponse = await getAddressListChildren({
      countryCode: departure_countryCode,
      parentAddressLevel: 1,
      parentAddressLanguage: language,
      parentAddressName: province,
      addressChildrenLevelNumber: 3,
    })

    const cities = citiesResponse?.data?.nodes || []

    return cities
  })()

  let districts = []
  if (isCityHidden) {
    /** 市隐藏的情况 省变化 -> 更新区 */
    districts = flatten(cities.map((item) => item.child))
  }

  form.setFieldsValue({
    departure_city: undefined,
    departure_district: undefined,
    departure_town: undefined,
  })
  await dispatch(
    updateAddressVO({
      departure_province: province,
      departure_city: undefined,
      departure_district: undefined,
      departure_town: undefined,
    })
  )

  await dispatch(actions.update({ cities, districts }))
}

/** 选择发货城市 */
export const selectSenderAddressCityNew = (value: string) => async (dispatch: AppDispatch, getState: AppGetState, form: FormInstance) => {
  const { senderAddressForm: state } = getState()
  const { cities } = state
  const { isOnlyDistrictShow, isCityHidden } = await dispatch(getSenderAddressVisibility())

  /* 只有区显示的情况 */
  if (isOnlyDistrictShow) {
    return flatten(cities.map((item) => item?.child)).filter(Boolean)
  }

  const districts = (() => {
    if (cities?.length) {
      /* 市隐藏的情况 */
      if (isCityHidden) {
        return flatten(cities.map((item) => item?.child)).filter(Boolean)
      }

      const city = cities.find((item) => item?.name === value)
      return city?.child || []
    }

    return []
  })()

  form.setFieldsValue({
    departure_district: undefined,
    departure_town: undefined,
  })
  await dispatch(
    updateAddressVO({
      departure_city: value,
      departure_district: undefined,
      departure_town: undefined,
    })
  )

  await dispatch(actions.update({ districts }))
}

/** 选择发货区 */
export const selectSenderAddressDistrictNew = (value: string) => async (dispatch: AppDispatch, getState: AppGetState, form: FormInstance) => {
  const { senderAddressForm: state } = getState()
  const { isCityHidden } = await dispatch(getSenderAddressVisibility())

  const { districts } = state
  const district = districts.find((item) => item.name === value)

  dispatch(updateAddressVO({ departure_district: value, departure_town: undefined }))
  // clear town
  form.setFieldsValue({
    departure_district: value,
    departure_town: undefined,
  })

  dispatch(
    actions.update({
      towns: district?.child || [],
    })
  )

  // 马来西亚旧数据如果有市，切换区后会带着错误的区过去
  if (isCityHidden) {
    form.setFieldsValue({ departure_city: undefined })
  }
}
